import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Switch, Spin } from "antd"; // Added Spin for loader
import { fetchUsers } from "../slices/userSlice";
import noDataImage from "./../assets/icons/nodataimage.svg";
import DashboardMetrics from "../components/DashboardMetrics";
import UserModel from "./../components/CreateUserModel";
import "./Users.css";
import { updateUserStatus } from "../services/api"; // Assume you have an API file for HTTP requests
import Swal from "sweetalert2";
import moment from "moment";
import TopBar from "./../components/TopBar";
import { ShimmerContentBlock, ShimmerDiv, ShimmerTable } from "shimmer-effects-react";
import { useNavigate } from "react-router-dom";

const Users = () => {
  const dispatch = useDispatch();
  const usersFromStore = useSelector((state) => state.users?.data || []);
  const [users, setUsers] = useState(usersFromStore);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false); // Added loading state
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "asc",
  });
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const convertToCSV = (data) => {
    const headers = [
      "No.",
      "Full Name",
      "Email ID",
      "Date",
      "User Type",
      "Status",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    data.forEach((user, index) => {
      const row = [
        index + 1, // Serial No.
        user.name || "N/A", // Full Name
        user.email || "N/A", // Email ID
        moment(user.createdAt).format("DD-MM-YY"), // Date
        user.password ? "Email" : "Google User", // User Type
        user.status, // Status (active/deactive)
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleDownloadCSV = () => {
    const csvData = convertToCSV(users); // Use the `users` state that contains the filtered data
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `users_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const itemsPerPage = 10;

  useEffect(() => {
    setLoading(true); // Set loading to true before fetching data
    dispatch(fetchUsers())
      .then(() => setLoading(false)) // Set loading to false after data is fetched
      .catch(() => setLoading(false));

    setSortConfig({ key: "createdAt", direction: "asc" }); // Default sort by "createdAt" ascending
  }, [dispatch]);

  useEffect(() => {
    applyFilters();
  }, [usersFromStore, filter, dateFilter, searchText]);

  const totalOrders = usersFromStore.reduce(
    (sum, user) => sum + (user.totalOrders || 0),
    0
  );
  const totalSpending = usersFromStore
    .reduce((sum, user) => sum + (user.totalSpending || 0), 0)
    .toFixed(2);
  const totalProcessingOrders = usersFromStore.reduce(
    (sum, user) => user.totalProcessingOrders || 0,
    0
  );

  const handleSwitchChange = (index) => {
    const user = users[index];
    const newStatus = user.status === "active" ? "deactive" : "active";

    // Show confirmation alert before updating the status
    Swal.fire({
      title: `Are you sure you want to set this user to ${newStatus}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, update it!",
      cancelButtonText: "No, cancel!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Update the state locally for immediate feedback
        const newUsers = users.map((user, idx) =>
          idx === index ? { ...user, status: newStatus } : user
        );
        setUsers(newUsers);

        // Call the API to update status
        updateUserStatus(user._id, newStatus)
          .then(() => {
            Swal.fire(
              "Updated!",
              `User status has been set to ${newStatus}.`,
              "success"
            );
          })
          .catch((error) => {
            Swal.fire(
              "Error!",
              "There was a problem updating the user status.",
              "error"
            );
          });
      }
    });
  };

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const updateUserData = () => {
    dispatch(fetchUsers());
  };

  // Sorting function
  const sortUsers = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    const sortedUsers = [...users].sort((a, b) => {
      if (key === "name" || key === "email" || key === "createdAt") {
        return direction === "asc"
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      } else {
        return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
      }
    });
    setUsers(sortedUsers);
    setSortConfig({ key, direction });
  };

  // Apply status and date filters
  const applyFilters = () => {
    let filteredUsers = usersFromStore;
    if (searchText) {
      filteredUsers = filteredUsers.filter(user =>
        user.name.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filter === "active") {
      filteredUsers = filteredUsers.filter((user) => user.status === "active");
    } else if (filter === "deactive") {
      filteredUsers = filteredUsers.filter(
        (user) => user.status === "deactive"
      );
    }

    if (dateFilter !== "all") {
      const today = moment();
      filteredUsers = filteredUsers.filter((user) => {
        const userDate = moment(user.createdAt);
        switch (dateFilter) {
          case "today":
            return userDate.isSame(today, "day");
          case "week":
            return userDate.isSame(today, "week");
          case "month":
            return userDate.isSame(today, "month");
          case "year":
            return userDate.isSame(today, "year");
          default:
            return true;
        }
      });
    }

    setUsers(filteredUsers);
  };

  // Pagination logic
  const totalPages = Math.ceil(users.length / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const paginatedUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Show loading animation or error message if needed
  if (loading) {
    return (
      <>
        <div className="flex items-center justify-between gap-8 p-5 pr-20">
          <ShimmerDiv mode="dark" height={100} width={700} rounded={1} border={0} />
          <ShimmerDiv mode="dark" height={100} width={300} rounded={1} border={0} />
        </div>
        <div className="p-5 grid gap-8  grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 items-center justify-between">
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
          <ShimmerDiv mode="dark" height={150} width={300} border={0} rounded={1} />
        </div>
        <ShimmerTable mode="dark" row={7} col={7} border={0} rounded={0.25} rowGap={16} colPadding={[10, 5, 10, 5]} />
      </>
    );
  }
  return (
    <>
      <TopBar searchPlaceholder="Search Users Here" onSearchChange={handleSearchChange} />
      <div className="p-6">
        <div className="users-container">
          <div className="metrics-section">
            <DashboardMetrics title="Total Users" count={usersFromStore.length} />
            <DashboardMetrics title="Total Orders" count={totalOrders} onClick={() => navigate("/orders")}/>
            <DashboardMetrics
              title="Total Spending"
              count={`€ ${Math.round(totalSpending)}`}
            />
            <DashboardMetrics
              title="Total Pending Orders"
              count={totalProcessingOrders} onClick={() => navigate("/orders")}
            />
          </div>
          <div className="search-create-section">
            {/* Dropdown for Activation Status */}
            <div className="dropdown-buttons">
              <div className="users-dropdown">
                <button className="users-dropdown-button clickable">
                  {filter === "all"
                    ? "User Status"
                    : filter === "active"
                      ? "Activated Users"
                      : "Deactivated Users"}
                  <div className="users-dropdown-content">
                    <a
                      href="#"
                      className={filter === "all" ? "active" : ""}
                      onClick={() => setFilter("all")}
                    >
                      All Users
                    </a>
                    <a
                      href="#"
                      className={filter === "active" ? "active" : ""}
                      onClick={() => setFilter("active")}
                    >
                      Activated Users
                    </a>
                    <a
                      href="#"
                      className={filter === "deactive" ? "active" : ""}
                      onClick={() => setFilter("deactive")}
                    >
                      Deactivated Users
                    </a>
                  </div>
                </button>
              </div>
              <div className="users-dropdown">
                <button className="users-dropdown-button">
                  {dateFilter === "all"
                    ? "Date Filter"
                    : dateFilter === "today"
                      ? "Today"
                      : dateFilter === "week"
                        ? "This Week"
                        : dateFilter === "month"
                          ? "This Month"
                          : "This Year"}
                  <div className="users-dropdown-content">
                    <a
                      href="#"
                      className={dateFilter === "all" ? "active" : ""}
                      onClick={() => setDateFilter("all")}
                    >
                      All Dates
                    </a>
                    <a
                      href="#"
                      className={dateFilter === "today" ? "active" : ""}
                      onClick={() => setDateFilter("today")}
                    >
                      Today
                    </a>
                    <a
                      href="#"
                      className={dateFilter === "week" ? "active" : ""}
                      onClick={() => setDateFilter("week")}
                    >
                      This Week
                    </a>
                    <a
                      href="#"
                      className={dateFilter === "month" ? "active" : ""}
                      onClick={() => setDateFilter("month")}
                    >
                      This Month
                    </a>
                    <a
                      href="#"
                      className={dateFilter === "year" ? "active" : ""}
                      onClick={() => setDateFilter("year")}
                    >
                      This Year
                    </a>
                  </div>
                </button>
              </div>
            </div>

            <Button
              type="primary"
              className="create-button clickable"
              onClick={toggleModal}
            >
              Create New User
            </Button>
          </div>

          {users.length > 0 ? (
            <div className="user-table-container">
              <table className="user-table">
                <thead>
                  <tr>
                    <th className="header-cell">No.</th>

                    <th
                      className="header-cell clickable"
                      onClick={() => sortUsers("name")}
                    >
                      <div className="clickable-header">
                        Full Name{" "}
                        {sortConfig.key === "name" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </div>
                    </th>
                    <th
                      className="header-cell clickable"
                      onClick={() => sortUsers("email")}
                    >
                      <div className="clickable-header">
                        Email ID{" "}
                        {sortConfig.key === "email" &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </div>
                    </th>
                    <th
                      className="header-cell clickable"
                      onClick={() => sortUsers("createdAt")}
                    >
                      <div className="clickable-header">
                        Date{" "}
                        {sortConfig.key === "createdAt"
                          ? sortConfig.direction === "asc"
                            ? "↑"
                            : "↓"
                          : sortConfig.key === null &&
                            sortConfig.direction === "asc"
                            ? "↑"
                            : ""}
                      </div>
                    </th>
                    <th className="header-cell">User Type</th>
                    <th className="header-cell">Activate/Deactivate</th>
                    <th className="header-cell">More</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user, index) => (
                    <tr key={user._id}>
                      <td className="body-cell clickable">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="body-cell">{user.name}</td>
                      <td className="body-cell">{user.email}</td>
                      <td className="body-cell">
                        {moment(user.createdAt).format("DD-MM-YY")}
                      </td>
                      <td className="body-cell">
                        {/* {user.password && user.password.length > 6
                      ? `${user.password.substring(
                          0,
                          3
                        )}...${user.password.substring(
                          user.password.length - 3
                        )}`
                      : user.password ?? "Google User"}
                     */}
                        {user.password ? "Email" : "Google User"}
                      </td>
                      <td className="body-cell">
                        <Switch
                          checked={user.status === "active"}
                          onChange={() => handleSwitchChange(index)}
                          style={{
                            backgroundColor:
                              user.status === "active" ? "#00BD79" : "#798593",
                          }}
                        />
                      </td>
                      <td className="body-cell">
                        <Link
                          to={`/users/${user._id}`}
                          className="more-link clickable"
                        >
                          More..
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination controls */}
              <div className="pagination-container mt-4 mx-auto justify-between px-1">
                <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                  &lt; Previous
                </button>
                &nbsp;
                <span className="">
                  Page {currentPage} of {totalPages}
                </span>
                &nbsp;
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next &gt;
                </button>
              </div>
              <div className="download-report">
                <button className="download-link" onClick={handleDownloadCSV}>
                  Download Report
                </button>
              </div>
            </div>
          ) : (
            <div className="no-data-section">
              <img src={noDataImage} alt="No data" className="no-data-image" />
              <div className="no-data-title">No Users to show right now!</div>
              <div className="no-data-description">
                We don't have any user's info to show here. Please add users first
                and then check their details here.
              </div>
            </div>
          )}

          {isModalVisible && (
            <div className="modal-overlay" onClick={toggleModal}>
              <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                <UserModel
                  closeModal={toggleModal}
                  updateUserData={updateUserData}
                />
              </div>
            </div>
          )}
        </div>
      </div></>
  );
};

export default Users;
